// src/components/SocialAccountsEditor.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

const defaultSocialAccounts = {
  youtube: 'https://www.youtube.com/@SpicifyOriginals',
  tiktok: 'https://www.tiktok.com/@spicifyoriginals?_r=1&_t=ZG-97aVV2hYMoD',
  instagram: 'https://www.instagram.com/spicifyoriginals?igsh=MWFyMXJwZ2ZldW9vMQ==',
  facebook: 'https://www.facebook.com/profile.php?id=61589339544740',
  wattpad: 'https://www.wattpad.com/SpicifyOriginals?utm_source=web&utm_medium=email&utm_content=share_profile',
  twitter: ''
};

const SocialAccountsEditor = () => {
  const [socialAccounts, setSocialAccounts] = useState(defaultSocialAccounts);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchSocialAccounts = async () => {
      try {
        const docRef = doc(db, 'siteContent', 'socialAccounts');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setSocialAccounts({
            ...defaultSocialAccounts,
            ...docSnap.data(),
          });
        }
      } catch (error) {
        console.error("Error fetching social accounts: ", error);
        setMessage('Error loading social accounts');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSocialAccounts();
  }, []);

  const handleInputChange = (platform) => (e) => {
    setSocialAccounts({
      ...socialAccounts,
      [platform]: e.target.value
    });
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      await setDoc(doc(db, 'siteContent', 'socialAccounts'), socialAccounts);
      setMessage('Social accounts updated successfully!');
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving social accounts: ", error);
      setMessage('Error saving social accounts');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">Social Media Accounts</h2>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
          >
            Edit
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-md ${
          message.includes('Error') ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
        }`}>
          {message}
        </div>
      )}

      <div className="space-y-4">
        {isEditing ? (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">YouTube</label>
              <input
                type="url"
                value={socialAccounts.youtube}
                onChange={handleInputChange('youtube')}
                placeholder="https://youtube.com/yourchannel"
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">TikTok</label>
              <input
                type="url"
                value={socialAccounts.tiktok}
                onChange={handleInputChange('tiktok')}
                placeholder="https://tiktok.com/@yourusername"
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
              <input
                type="url"
                value={socialAccounts.instagram}
                onChange={handleInputChange('instagram')}
                placeholder="https://instagram.com/yourusername"
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
              <input
                type="url"
                value={socialAccounts.facebook}
                onChange={handleInputChange('facebook')}
                placeholder="https://facebook.com/yourpage"
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Wattpad</label>
              <input
                type="url"
                value={socialAccounts.wattpad || ''}
                onChange={handleInputChange('wattpad')}
                placeholder="https://www.wattpad.com/user/yourusername"
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Twitter</label>
              <input
                type="url"
                value={socialAccounts.twitter}
                onChange={handleInputChange('twitter')}
                placeholder="https://twitter.com/yourusername"
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div> */}
          </>
        ) : (
          <div className="flex flex-wrap gap-4">
            {socialAccounts.youtube && (
              <a href={socialAccounts.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center text-red-600 hover:text-red-800">
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                YouTube
              </a>
            )}
            {socialAccounts.tiktok && (
              <a href={socialAccounts.tiktok} target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-800 hover:text-black">
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                </svg>
                TikTok
              </a>
            )}
            {socialAccounts.instagram && (
              <a href={socialAccounts.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center text-pink-600 hover:text-pink-800">
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
                Instagram
              </a>
            )}
            {socialAccounts.facebook && (
              <a href={socialAccounts.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800">
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.593 1.323-1.325V1.325C24 .593 23.407 0 22.675 0z"/>
                </svg>
                Facebook
              </a>
            )}
            {socialAccounts.wattpad && (
              <a href={socialAccounts.wattpad} target="_blank" rel="noopener noreferrer" className="flex items-center text-orange-600 hover:text-orange-800">
                <span className="w-6 h-6 mr-2 rounded bg-orange-600 text-white flex items-center justify-center text-sm font-bold">
                  W
                </span>
                Wattpad
              </a>
            )}

            {/* {socialAccounts.twitter && (
              <a href={socialAccounts.twitter} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-400 hover:text-blue-600">
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
                Twitter
              </a>
            )} */}
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialAccountsEditor;
